import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { XlsxFile } from '@/utils/xslx';
import { downloadFile } from '@/utils/file/utils';
import { useLazyGetCustomerListForExportQuery } from '@/services/innovorder/customer/customer.endpoints';
import { useGetBrandQuery } from '@/services/innovorder/brand/brand.endpoints';
import { getUserToken } from '@/redux/user';
import { CustomerForExport } from '@/services/innovorder/customer/customer.types';

// Template headers for NEW customer import (no customer ID since they don't exist yet)
// Must match V1 backoffice template format
const TEMPLATE_HEADERS = [
    'Email',
    'Mot de passe',
    'Prénom',
    'Nom',
    'Téléphone',
    'Code pin',
    'N° badge',
    'N° convive',
    'Groupe',
    'Section',
    'Sous-section',
    'Type de compte',
    'Code tarif',
    'Code subvention',
    'Code admission',
    'Code régime tarifaire',
    'Montant découvert personnalisé',
    'Solde',
    "Date d'archivage",
    "Date d'arrivée",
    'Date de départ',
];

// Export headers for EXISTING customers (includes customer ID for updates)
// Match V1 backoffice export format (no Solde column)
const EXPORT_HEADERS = [
    'Identifiant client',
    'Email',
    'Mot de passe',
    'Prénom',
    'Nom',
    'Téléphone',
    'Code pin',
    'N° badge',
    'N° convive',
    'Groupe',
    'Section',
    'Sous-section',
    'Type de compte',
    'Code tarif',
    'Code subvention',
    'Code admission',
    'Code régime tarifaire',
    'Montant découvert personnalisé',
    "Date d'archivage",
    "Date d'arrivée",
    'Date de départ',
];

type UseUploadStepVMProps = {
    brandId: number;
};

const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR');
};

// Row format must match EXPORT_HEADERS order (no balance/Solde column to match V1)
const customerToRow = (customer: CustomerForExport, birthdate: boolean): (string | number)[] => {
    const row: (string | number)[] = [
        customer.customerId ?? '',
        customer.email ?? '',
        '', // password - empty for security
        customer.firstName ?? '',
        customer.lastName ?? '',
        customer.phone ?? '',
        '', // pinCode - empty for security
        customer.badgeNumber ?? '',
        customer.studentNumber ?? '',
        customer.class ?? '',
        customer.section ?? '',
        customer.subSection ?? '',
        customer.accountType === 'BANKDEBIT' ? 'BANKDEBIT' : customer.accountPaymentType ?? '',
        customer.tariffCode ?? '',
        customer.grantCode ?? '',
        customer.entranceFeeCode ?? '',
        customer.pricingRuleCode ?? '',
        customer.ewalletOverdraftAmount ?? '',
        formatDate(customer.archivedAt),
        formatDate(customer.accessFrom),
        formatDate(customer.accessUntil),
    ];

    if (birthdate) {
        row.push(formatDate(customer.birthdate));
    }

    return row;
};

export const useUploadStepVM = ({ brandId }: UseUploadStepVMProps) => {
    const token = useSelector(getUserToken);
    const [isExporting, setIsExporting] = useState(false);
    const [getCustomerListForExport] = useLazyGetCustomerListForExportQuery();
    const { data: brand } = useGetBrandQuery({ brandId, token });

    const hasBirthdate = useCallback(() => !!brand?.options?.BIRTHDATE?.isActivated, [brand]);

    const onDownloadTemplate = async () => {
        const headers = [...TEMPLATE_HEADERS];
        if (hasBirthdate()) {
            headers.push("Date d'anniversaire");
        }

        const xslx = new XlsxFile();
        xslx.addWorksheet('customer', [headers]);

        const data = xslx.toArrayBuffer();
        downloadFile(
            'Import_clients_template.xlsx',
            data,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        );
    };

    const onExportCustomers = async () => {
        if (isExporting) return;

        setIsExporting(true);
        try {
            // Match V1 exactly: /customers?brandId=X&include=orderStats,optIn,badges
            // V1 doesn't use pagination - API returns all customers by default
            const result = await getCustomerListForExport({
                brandId,
                token,
                getArchivedCustomers: false,
                getDisabledCustomers: false,
                emailDomainNames: null,
                groups: null,
                sections: null,
                subsections: null,
            }).unwrap();

            const birthdate = hasBirthdate();
            const headers = [...EXPORT_HEADERS];
            if (birthdate) {
                headers.push("Date d'anniversaire");
            }

            // API returns { count, customers } not a direct array
            const customers = Array.isArray(result) ? result : (result as { customers: CustomerForExport[] }).customers;
            const rows = [headers, ...customers.map((c) => customerToRow(c, birthdate))];

            const xslx = new XlsxFile();
            xslx.addWorksheet('customer', rows);

            const data = xslx.toArrayBuffer();
            const brandName = brand?.name?.replace(/\s+/g, '_') ?? 'export';
            const filename = `Base_client_update_${brandName}.xlsx`;
            downloadFile(filename, data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        } catch (error) {
            console.error('Failed to export customers:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return { onDownloadTemplate, onExportCustomers, isExporting };
};
